/* eslint-disable no-param-reassign */
import { LitElement, html, css } from 'lit';
import { jhaStyles } from '../../../styles/jha-styles.js';
import '../../jha-list/jha-list.js';

const ITEM_SELECTOR = 'jha-reorder-list-item';

const KEYS = {
  ARROW_DOWN: 'ArrowDown',
  ARROW_UP: 'ArrowUp',
  SPACE: 'Space',
};

function pointWithinRect(x, y, rect) {
  const top = rect.top + window.pageYOffset;
  const bottom = rect.bottom + window.pageYOffset;
  return rect.left <= x && rect.right >= x && top <= y && bottom >= y;
}

const styles = css`
  :host {
    display: block;
  }
  jha-list {
    margin-bottom: 0;
  }
  @media (max-width: 768px) {
    jha-list[organize-list] {
      padding: 1px 0 8px;
    }
  }
  .assistive-text {
    position: absolute !important;
    margin: -1px !important;
    border: 0 !important;
    padding: 0 !important;
    width: 1px !important;
    height: 1px !important;
    overflow: hidden !important;
    clip: rect(0 0 0 0) !important;
    text-transform: none !important;
    white-space: nowrap !important;
  }
  svg {
    display: block;
    width: 24px;
    height: 24px;
    fill: var(--jha-list-organizer-handle-color, var(--jha-text-theme));
  }
  ::slotted([selected='true']) {
    --jha-component-background-color: var(
      --jha-focus-highlight-color,
      var(--primary-content-background-color)
    );
  }
`;

export default class JhaReorderList extends LitElement {
  static get styles() {
    return [jhaStyles, styles];
  }

  static get properties() {
    return {
      ariaHelpText: {
        type: String,
      },
      ariaLabel: {
        type: String,
      },
    };
  }

  constructor() {
    super();
    this.ariaHelpText = '';
    this.ariaLabel = '';
    this._listItemEvents = [
      ['dragstart', this.dragStart_.bind(this)],
      ['dragend', this.dragEnd_.bind(this)],
      ['drop', this.drop_.bind(this)],
      ['dragenter', this.dragEnter_.bind(this)],
      ['dragover', this.dragOver_.bind(this)],
      ['keydown', this.handleKeydown_.bind(this)],
    ];

    this._dragAreaEvents = [
      ['mousedown', this.enableDrag_.bind(this)],
      ['mouseup', this.disableDrag_.bind(this)],
      ['touchstart', this.startDragFromTouch_.bind(this)],
      ['touchmove', this.touchDragMove_.bind(this)],
      ['touchend', this.endDragFromTouch_.bind(this)],
    ];

    this.dragTarget_ = undefined;
    this.keyReorderTarget_ = undefined;
    this.dragHoverElem_ = undefined;
    this.draggingEnabled_ = false;
    this.dragClone_ = undefined;
    this.dragCloneOffsets_ = undefined;
    this.listLength_ = undefined;
    this.boundOnKeyReorderTargetBlur_ = this.onKeyReorderTargetBlur_.bind(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeListeners(this.listItems);
  }

  /** @return {Array<import('../jha-reorder-list-item/jha-reorder-list-item.js').default>} */
  get listItems() {
    return Array.from(this.querySelectorAll(ITEM_SELECTOR)) || [];
  }

  handleSlotchange() {
    this.requestUpdate();
    window.requestAnimationFrame(() => {
      this.updateSlottedContent();
    });
  }

  updateSlottedContent() {
    const items = this.listItems;
    if (!items || this.listLength_ === items.length) return;

    items.forEach(item => {
      item.setAttribute('draggable', items.length > 1 ? 'true' : 'false');
    });

    if (this.listLength_) {
      this.removeListeners(items);
    }

    this.listLength_ = items.length;
    this.addEventListeners(items);
    this.dispatchChangeEvent();
  }

  addEventListeners(items) {
    items.forEach(item => {
      this._listItemEvents.forEach(evtHandler => {
        item.addEventListener(...evtHandler, false);
      });

      // set tabIndex
      item.setAttribute('tabindex', 0);
      item.setAttribute('role', 'option');
      const dragArea = item.shadowRoot.querySelector('div[drag-item]');
      if (dragArea) {
        this._dragAreaEvents.forEach(evtHandler => {
          dragArea.addEventListener(...evtHandler, false);
        });
      }
    });
  }

  removeListeners(items) {
    items.forEach(item => {
      this._listItemEvents.forEach(evtHandler => {
        item.removeEventListener(...evtHandler, false);
      });

      const dragArea = item.shadowRoot.querySelector('div[drag-item]');
      if (dragArea) {
        this._dragAreaEvents.forEach(evtHandler => {
          dragArea.removeEventListener(...evtHandler, false);
        });
      }
    });
  }

  /**
   * @description navigate list and allow reorder on key actions
   * @param {!KeyboardEvent} evt
   */
  handleKeydown_(evt) {
    if (
      !['JHA-LIST-ITEM', 'JHA-REORDER-LIST-ITEM'].includes(
        /** @type {HTMLElement} */ (evt.target).tagName
      )
    ) {
      return;
    }

    const { code } = evt;
    const item = evt
      .composedPath()
      .find(
        el =>
          /** @type {!HTMLElement} */ (el).tagName ===
          ITEM_SELECTOR.toUpperCase()
      );

    switch (code) {
      case KEYS.ARROW_DOWN:
      case KEYS.ARROW_UP:
        evt.preventDefault(); // Prevent page scroll
        this.handleArrowKey(item, code);
        break;
      case KEYS.SPACE:
        evt.preventDefault(); // Prevent page scroll
        this.toggleSelected(item);
        break;
      default:
        break;
    }
  }

  handleArrowKey(item, direction) {
    const curIndex = this.getItemIndex(item);
    const newItemIndex = curIndex + (direction === KEYS.ARROW_UP ? -1 : 1);

    if (newItemIndex < 0 || newItemIndex >= this.listItems.length) return;

    const otherItem = this.listItems[newItemIndex];

    if (this.keyReorderTarget_) {
      this.moveListItem(item, otherItem, direction);
      return;
    }

    this.moveFocus(item, otherItem);
    this.ariaHelpText = '';
  }

  moveFocus(item, otherItem) {
    this.blurItem(item);
    this.focusItem(otherItem);
  }

  moveListItem(item, otherItem, direction) {
    const refItem = direction === KEYS.ARROW_UP ? item.nextSibling : item;
    item.parentNode.insertBefore(otherItem, refItem);
    this.updateAriaHelpText(item);
  }

  /** @param {?HTMLElement} item */
  focusItem(item) {
    if (item) {
      item.tabIndex = 0;
      item.focus();
    }
  }

  /** @param {?HTMLElement} item */
  blurItem(item) {
    if (item) {
      item.blur();
      item.tabIndex = -1;
    }
  }

  toggleSelected(item) {
    this.listItems.forEach(listItem => {
      listItem.removeAttribute('selected');
    });

    if (this.keyReorderTarget_) {
      this.keyReorderTarget_.removeEventListener(
        'blur',
        this.boundOnKeyReorderTargetBlur_,
        false
      );
      this.resetTabIndexes();
      this.keyReorderTarget_ = undefined;
      this.updateAriaHelpText(item);
      this.dispatchChangeEvent();
      return;
    }

    this.keyReorderTarget_ = item;
    this.keyReorderTarget_.setAttribute('selected', 'true');
    this.keyReorderTarget_.addEventListener(
      'blur',
      this.boundOnKeyReorderTargetBlur_,
      false
    );
    this.updateAriaHelpText(item);
  }

  onKeyReorderTargetBlur_() {
    this.resetTabIndexes();
    this.toggleSelected(this.keyReorderTarget_);
    this.ariaHelpText = '';
  }

  resetTabIndexes() {
    if (this.keyReorderTarget_) {
      this.keyReorderTarget_.setAttribute('tabIndex', -1);
    }
    this.listItems[0].tabIndex = 0;
  }

  updateAriaHelpText(target) {
    const title = target.title || 'Item';
    const position = `${this.getItemIndex(target) + 1} of ${
      this.listItems.length
    }`;
    const actionText = this.keyReorderTarget_
      ? `selected, current`
      : `dropped, final`;
    this.ariaHelpText = `${title} ${actionText} position ${position}`;
  }

  enableDrag_() {
    this.draggingEnabled_ = true;
  }

  disableDrag_() {
    this.draggingEnabled_ = false;
  }

  /**
   * Drag-and-drop apis aren't compatible with touch events. We
   * have to manually make a clone of the element and move it's
   * position.
   */
  startDragFromTouch_(evt) {
    const e = /** @type {!TouchEvent} */ (evt);
    e.preventDefault();
    const target = /** @type {HTMLElement} */ (e.currentTarget);
    const listItem =
      target.closest(ITEM_SELECTOR) ||
      // @ts-ignore
      target.getRootNode().host;

    if (!listItem) {
      return;
    }

    const listItemRect = listItem.getBoundingClientRect();
    const listItemClone = listItem.cloneNode(true);
    listItemClone.style.width = `${listItemRect.width}px`;
    listItemClone.style.height = `${listItemRect.height}px`;
    listItemClone.style.top = `${
      listItemRect.top + window.pageYOffset - window.scrollY
    }px`;
    listItemClone.style.left = `${listItemRect.left}px`;
    listItemClone.style.zIndex = '1';
    listItemClone.style.position = 'fixed';
    listItemClone.setAttribute('clone', true);

    this.dragClone_ = listItemClone;
    listItem.parentNode.appendChild(listItemClone);

    this.dragCloneOffsets_ = {
      left: e.touches[0].pageX - listItemRect.left,
      top: e.touches[0].pageY - listItemRect.top,
    };
    listItem.setAttribute('drag', true);
    this.dragTarget_ = listItem;
  }

  /**
   * Update the clone to stay under the touch - use the same relative
   * offsets that were used to initiate the drag
   */
  touchDragMove_(evt) {
    const e = /** @type {!TouchEvent} */ (evt);
    e.preventDefault();
    if (!this.dragClone_) {
      return;
    }

    const top =
      e.touches[0].pageY -
      this.dragCloneOffsets_.top +
      window.pageYOffset -
      window.scrollY;
    const left = e.touches[0].pageX - this.dragCloneOffsets_.left;
    this.dragClone_.style.top = `${top}px`;
    this.dragClone_.style.left = `${left}px`;

    if (
      pointWithinRect(
        e.touches[0].pageX,
        e.touches[0].pageY,
        this.dragTarget_.getBoundingClientRect()
      )
    ) {
      return;
    }

    if (!this.dragHoverElem_) {
      this.moveDraggedItem_(e);
    }
  }

  endDragFromTouch_(evt) {
    if (this.dragClone_ && this.dragClone_.parentNode) {
      this.dragClone_.parentNode.removeChild(this.dragClone_);
      this.dragClone_ = undefined;
      this.dragEnd_(/** @type {!TouchEvent} */ (evt));
    }
  }

  /**
   * We're actually going to drag this element, so disable text selection
   * and save a reference to the list item being dragged. Other events only
   * provide the element being hovered over.
   */
  dragStart_(evt) {
    if (!this.draggingEnabled_) {
      evt.preventDefault();
      return;
    }

    const listItem = /** @type {!Element} */ (evt.currentTarget);
    // We can't modify the DOM directly in this handler due to bugs in Safari
    // But for some reason a timeout is ok
    setTimeout(() => {
      // Prevent text from being selected while dragging
      document.body.style.userSelect = 'none';
      listItem.setAttribute('drag', 'true');
    }, 0);
    this.dragTarget_ = listItem;
    evt.dataTransfer.dropEffect = 'move';
    evt.dataTransfer.setData('text/plain', evt.target.itemId);
  }

  /**
   * @param {!DragEvent} evt
   * @private
   */
  dragEnter_(evt) {
    if (
      /** @type {!HTMLElement} */ (evt.currentTarget).tagName ===
      ITEM_SELECTOR.toUpperCase()
    ) {
      evt.stopPropagation();
    } else {
      return;
    }

    evt.dataTransfer.dropEffect = 'move';

    if (
      !this.dragTarget_ ||
      !this.dragTarget_.parentNode ||
      !this.dragTarget_.draggable ||
      this.dragHoverElem_ === evt.currentTarget
    ) {
      return;
    }
    this.moveDraggedItem_(evt);
  }

  /**
   * As opposed to traditional drag-and-drop, we're going to move
   * and animate the element as soon as it's over another valid location.
   *
   * We remove the element being hovered over, and re-insert it into the dom.
   * Then we use transforms to offset it back to it's old location, and transition
   * the transform back to zero.
   */
  moveDraggedItem_(evt) {
    const itemList = this.listItems;
    const targetElement = this.findTarget(evt, itemList);

    if (!targetElement || !targetElement.itemId) return;

    const curIndex = this.getItemIndex(this.dragTarget_);
    const refIndex = this.getItemIndex(targetElement);

    if (itemList.length - 1 === refIndex) {
      targetElement.parentNode.appendChild(this.dragTarget_);
    } else {
      const refNode =
        refIndex > curIndex ? targetElement.nextSibling : targetElement;
      targetElement.parentNode.insertBefore(this.dragTarget_, refNode);
    }
  }

  drop_(evt) {
    evt.stopPropagation();
    evt.preventDefault();
  }

  dragOver_(evt) {
    evt.preventDefault();
    evt.dataTransfer.dropEffect = 'move';
  }

  /**
   * Dragging is over - clean up. Assign the sortIndex property of each item
   * based on its current order in the DOM. Then manually resort
   * items and update the itemIndexes map to keep everything consistent.
   */
  dragEnd_(evt) {
    if (!evt.touches && !this.draggingEnabled_) {
      evt.preventDefault();
      return;
    }
    const listItem = this.dragTarget_;

    if (listItem) {
      listItem.removeAttribute('drag');
    }

    // Dragging done; un-disable text selection
    document.body.style.removeProperty('-webkit-user-select');
    document.body.style.removeProperty('-moz-user-select');
    document.body.style.removeProperty('user-select');

    this.dragTarget_ = undefined;
    this.dragHoverElem_ = undefined;

    this.dispatchChangeEvent();
  }

  dispatchChangeEvent() {
    this.dispatchEvent(
      new CustomEvent('reorder', {
        bubbles: true,
        composed: true,
        detail: this.getItemIdArray(),
      })
    );
  }

  getItemIdArray() {
    return (Array.from(this.listItems) || []).map(item => item.itemId);
  }

  getItemIndex(item) {
    const items = this.listItems;
    if (!items) return null;

    return items.indexOf(item);
  }

  findTarget(evt, itemList = []) {
    if (evt.touches) {
      return itemList.find(item => {
        const targetRect = item.getBoundingClientRect();
        return pointWithinRect(
          evt.touches[0].pageX,
          evt.touches[0].pageY,
          targetRect
        );
      });
    }

    if (
      evt.currentTarget &&
      evt.currentTarget.tagName === ITEM_SELECTOR.toUpperCase() &&
      !evt.currentTarget.hasAttribute('drag')
    ) {
      return evt.currentTarget;
    }

    return null;
  }

  render() {
    return html`
      <div id="reorderInstructions" class="assistive-text">
        Press space bar to select an item then use the up and down arrow keys to
        move the selected item.
        <div aria-live="assertive">${this.ariaHelpText}</div>
      </div>
      <jha-list
        organize-list
        role="listbox"
        aria-describedby="reorderInstructions"
        aria-label=${this.ariaLabel}
        tabindex="0"
      >
        <slot @slotchange=${this.handleSlotchange}></slot>
      </jha-list>
    `;
  }
}

export { styles as JhaReorderListStyles };
